import { Button } from "../ui/button"
import { FC, useCallback, useState, useEffect, useMemo } from "react"
import { useSubjectSelector } from "@/common"
import { notificationStore } from "@/store/notification-store"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog"
import { Label } from "../ui/label"
import { Input } from "../ui/input"
import { Checkbox } from "../ui/checkbox"
import { EmailChipsInput } from "../ui/email-chips-input"
import { BaseTemplateTags, EditMessageContext, NotificationEntityType, RecipientType, ReminderRecipientGroupCodes, ReminderTriggerPeriod, ReminderType } from "@/models/enum"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select"
import { Calendar, Clock, FileText, Loader2 } from "lucide-react"
import { timezoneOptions } from "@/lib/mock-data"
import { Switch } from "../ui/switch"
import { useForm, Controller, FormProvider } from "react-hook-form"
import { CreateReminderForm, RegularReminder, Reminder, TemplateConfiguration } from "@/models/interface"
import { useNotificationCenterApi } from "@/hooks"
import { applySupportEmail, applySupportEmailPlaceholder, generateTimeOptions, processTemplateContent } from "@/lib/utils"
import useTranslation from "@/hooks/use-translation"
import {
    getDefaultReminderValues, 
    getFinalRecipientCodes,
    getInternalRecipientCheckboxOptions,
    normalizeEntityTypes,
    getReminderCategoryFromEntityTypes,
    getDefaultTriggerType
} from "@/lib/reminder-field-mapping"
import { toast } from "@/hooks/use-toast"

export const CreateReminderDialog: FC<{
    open: boolean,
    setAddReminderOpen: React.Dispatch<React.SetStateAction<boolean>>
    onEditMessage: (template: TemplateConfiguration, context: EditMessageContext) => void
    onSaveSuccess?: () => void
}> = ({open, setAddReminderOpen, onEditMessage, onSaveSuccess}) => {
    const { t, language } = useTranslation()
    const [withSampleData, setWithSampleData] = useState(true)
    const [templatesLoading, setTemplatesLoading] = useState(false)
    const [isLoading, setIsLoading] = useState(false)

    const { selectedRecipientType, templates, baseTemplate, newlyCreatedTemplateId, feedbackEmailAddress } = useSubjectSelector(notificationStore,
        ['selectedRecipientType', 'templates', 'baseTemplate', 'newlyCreatedTemplateId', 'feedbackEmailAddress'])
    
    const isWorkerCategory = selectedRecipientType === RecipientType.WORKER
    const isInternalUsersCategory = selectedRecipientType === RecipientType.INTERNAL

    // Helper function to get English template content (index 0)
    const getEnglishTemplate = (template: TemplateConfiguration) => {
        return template.Data?.EmailTemplates?.[0]
    }    

    const { createReminderConfiguration, fetchTemplateConfigurations, fetchFeedbackEmailAddress } = useNotificationCenterApi()

    const formInstance = useForm<CreateReminderForm>({
        defaultValues: (() => {
            const entityTypes = isInternalUsersCategory ? [NotificationEntityType.TIMESHEET_EXPENSE] : [NotificationEntityType.TIMESHEET]
            const defaultCategory = getDefaultTriggerType(selectedRecipientType || "")
            const defaults = getDefaultReminderValues(selectedRecipientType || "", entityTypes, defaultCategory)
            
            return {
                ReminderTitle: "",
                ReminderEnabled: true,
                EntityTypes: entityTypes,
                RecipientType: selectedRecipientType || "",
                ReminderCategory: defaults.reminderCategory,
                RegularReminder: {
                    RecipientGroupCodes: isWorkerCategory 
                    ? [ReminderRecipientGroupCodes.AllWorkers] 
                    : defaults.recipientCodes,
                    AdditionalRecipientEmails: [],
                    ReminderTemplateId: "",
                    ReminderTrigger: {
                        NumberOfUnit: 1,
                        UnitType: "BusinessDay",
                        TriggerType: defaults.triggerType || (isWorkerCategory ? ReminderTriggerPeriod.BeforePeriodEnds 
                            : ReminderTriggerPeriod.AfterSubmission),
                        Time: "09:00",
                        TimeZone: "America/New_York"
                    }
                },
                // Internal recipient fields
                InternalRecipientCheckboxes: defaults.internalRecipientCheckboxes,
            }
        })()
    })
    const { handleSubmit, register, watch, setValue, control, reset } = formInstance

    
    const selectedTemplateId = watch('RegularReminder.ReminderTemplateId')
    const selectedWhat = watch('EntityTypes.0')
    const reminderTitle = watch('ReminderTitle')
    const selectedWorkerGroupCode = watch('RegularReminder.RecipientGroupCodes.0')
    const internalRecipientCheckboxes = watch('InternalRecipientCheckboxes')
    const numberOfUnits = watch('RegularReminder.ReminderTrigger.NumberOfUnit')

    // Comprehensive form validation for all visible UI fields
    const isFormValid: boolean = useMemo((): boolean => {
        // Always required fields
        if (!reminderTitle?.trim()) return false
        if (!selectedTemplateId) return false
        if(Number.isNaN(numberOfUnits)) return false

        // For non-worker categories (clients/internal), entity type selection is required
        if (!isWorkerCategory && !selectedWhat) return false

        // For workers, recipient group code is required
        if (isWorkerCategory && !selectedWorkerGroupCode) return false

        // For internal users, at least one checkbox must be checked
        if (isInternalUsersCategory) {
            const hasCheckedRecipient = internalRecipientCheckboxes && 
                Object.values(internalRecipientCheckboxes).some(checked => checked === true)
            if (!hasCheckedRecipient) return false
        }

        return true
    }, [reminderTitle, 
        selectedTemplateId, 
        isWorkerCategory, 
        selectedWhat, 
        selectedWorkerGroupCode, 
        isInternalUsersCategory, 
        numberOfUnits,
        JSON.stringify(internalRecipientCheckboxes)])

    const isSubmitDisabled = !isFormValid

    // Listen for newly created templates and switch to them
    useEffect(() => {
        if (newlyCreatedTemplateId && open) {
            setValue('RegularReminder.ReminderTemplateId', newlyCreatedTemplateId)
            notificationStore.clearNewlyCreatedTemplateId()
        }
    }, [newlyCreatedTemplateId, open, setValue])

    // Helper functions
    const getAvailableTemplatesForEntityTypes = (entityTypes: string[], reminderType: ReminderType): TemplateConfiguration[] => {
        let effectiveEntityTypes = entityTypes
        
        if (!effectiveEntityTypes || effectiveEntityTypes.length === 0) {
            // For workers, default to timesheet if no entity types specified
            if (isWorkerCategory) {
                effectiveEntityTypes = [NotificationEntityType.TIMESHEET]
            } else {
                return []
            }
        }

        // Convert TIMESHEET_EXPENSE to actual entity types array
        const expandedEntityTypes: string[] = []
        effectiveEntityTypes.forEach((entityType: string) => {
            if (entityType === NotificationEntityType.TIMESHEET_EXPENSE) {
                // TIMESHEET_EXPENSE means both timesheet and expense
                expandedEntityTypes.push(NotificationEntityType.TIMESHEET)
                expandedEntityTypes.push(NotificationEntityType.EXPENSE_CLAIM)
            } else {
                expandedEntityTypes.push(entityType)
            }
        })
        
        const reminderCategory = getReminderCategoryFromEntityTypes(selectedRecipientType || "", expandedEntityTypes, watch('RegularReminder.ReminderTrigger.TriggerType'))
        return templates
            .filter(template =>
                template?.Data?.RecipientType === selectedRecipientType
                && template?.Data?.TemplateType === reminderType
                && template?.Data?.ReminderCategories?.includes(reminderCategory))
    }

    // Generate time options with 1 Hour intervals
    const timeOptions = generateTimeOptions()

    const createReminder = useCallback((data: CreateReminderForm) => {
        setIsLoading(true)
        
        // Transform the data to match ReminderConfiguration structure
        const EntityTypes = normalizeEntityTypes(data.EntityTypes)
        
        // Get recipient codes based on category and entity types
        let recipientGroupCodes: string[] = []
        
        if (isWorkerCategory) {
            // For workers, use the selected recipient codes
            recipientGroupCodes = getFinalRecipientCodes(
                data.RecipientType, 
                data.EntityTypes, 
                data.RegularReminder.RecipientGroupCodes
            )
        } else if (isInternalUsersCategory) {
            // For internal, collect checked recipients and additional email
            const selectedCheckboxes = Object.entries(data.InternalRecipientCheckboxes || {})
                .filter(([_, checked]) => checked)
                .map(([code, _]) => code)
            
            recipientGroupCodes = getFinalRecipientCodes(
                data.RecipientType,
                data.EntityTypes,
                selectedCheckboxes
            )
        } else {
            // For clients, determine automatically based on entity types
            recipientGroupCodes = getFinalRecipientCodes(
                data.RecipientType,
                data.EntityTypes,
                []
            )
        }

        // Belt-and-suspenders: Enforce trigger type for internal expense claims
        const selectedWhat = data.EntityTypes?.[0]
        const isInternalExpense = data.RecipientType === RecipientType.INTERNAL 
            && selectedWhat === NotificationEntityType.EXPENSE_CLAIM
        const effectiveTriggerType = isInternalExpense 
            ? ReminderTriggerPeriod.AfterSubmission 
            : data?.RegularReminder?.ReminderTrigger?.TriggerType

        const RegularReminder: RegularReminder = {
            ...data.RegularReminder,
            RecipientGroupCodes: recipientGroupCodes,
            ReminderTrigger: {
                ...data.RegularReminder.ReminderTrigger,
                TriggerType: effectiveTriggerType
            }
        }

        const reminder: Reminder = {
            ReminderTitle: data.ReminderTitle,
            ReminderEnabled: data.ReminderEnabled,
            EntityTypes,
            RecipientType: data.RecipientType,
            ReminderCategory: getReminderCategoryFromEntityTypes(data.RecipientType, data.EntityTypes, effectiveTriggerType),
            RegularReminder
        }
        
        createReminderConfiguration(reminder).then(() => {
            setIsLoading(false)
            setAddReminderOpen(false)
            
            // Trigger refresh in parent component
            if (onSaveSuccess) {
                onSaveSuccess()
            }
        }).catch(() => {
            setIsLoading(false)
            toast({
                description: 'Error creating reminder',
                variant: "destructive"
            })
        })
    }, [setAddReminderOpen, isWorkerCategory, isInternalUsersCategory])

    // Load templates from API
    useEffect(() => {
        if (open) {
            setTemplatesLoading(true)
            fetchFeedbackEmailAddress()
            fetchTemplateConfigurations()
                .finally(() => {
                    setTemplatesLoading(false)
                })
        }
    }, [open])

    // Reset form when dialog opens
    useEffect(() => {
        if (open) {
            const entityTypes = isInternalUsersCategory ? [NotificationEntityType.TIMESHEET_EXPENSE] : [NotificationEntityType.TIMESHEET]
            const defaultCategory = getDefaultTriggerType(selectedRecipientType || "")
            const defaults = getDefaultReminderValues(selectedRecipientType || "", entityTypes, defaultCategory)
            
            reset({
                ReminderTitle: "",
                ReminderEnabled: true,
                EntityTypes: entityTypes,
                RecipientType: selectedRecipientType || "",
                ReminderCategory: defaults.reminderCategory,
                RegularReminder: {
                    RecipientGroupCodes: isWorkerCategory 
                    ? [ReminderRecipientGroupCodes.AllWorkers] 
                    : defaults.recipientCodes,
                    AdditionalRecipientEmails: [],
                    ReminderTemplateId: "",
                    ReminderTrigger: {
                        NumberOfUnit: 1,
                        UnitType: "BusinessDay",
                        TriggerType: defaults.triggerType || (isWorkerCategory ? ReminderTriggerPeriod.BeforePeriodEnds 
                            : ReminderTriggerPeriod.AfterSubmission),
                        Time: "09:00",
                        TimeZone: "America/New_York"
                    }
                },
                // Internal recipient fields
                InternalRecipientCheckboxes: defaults.internalRecipientCheckboxes,
            })
            setIsLoading(false)
            setWithSampleData(true)
        }
     }, [open, selectedRecipientType, isWorkerCategory, isInternalUsersCategory, reset])

    return (
        <Dialog open={open} onOpenChange={setAddReminderOpen}>
            <DialogContent className="max-w-7xl">
                <FormProvider {...formInstance}>
                    <form onSubmit={handleSubmit(createReminder)}>
                        <div className="h-[90vh] overflow-hidden p-0 flex flex-col">
                            <DialogHeader className="px-6 py-4 border-b">
                                <DialogTitle>{t("dashboard.createReminder.title")}</DialogTitle>
                            </DialogHeader>
                            
                            <div className="flex flex-1 overflow-hidden">
                                {/* Left Side - Form */}
                                <div className="w-3/6 p-6 overflow-y-auto border-r">
                                    <div className="space-y-6">
                                        {/* Title */}
                                        <div className="space-y-2">
                                            <Label htmlFor="add-reminder-title">
                                                {t("dashboard.createReminder.titleLabel")}
                                                <span className="text-sm text-red-500 ml-1">*</span>
                                            </Label>
                                            <Input
                                                id="add-reminder-title"
                                                {...register("ReminderTitle")}
                                            />
                                        </div>

                                        {/* What - Only show for clients */}
                                        {!isWorkerCategory && (
                                            <div className="space-y-2">
                                            <Label htmlFor="add-reminder-what">{t("dashboard.createReminder.whatLabel")}</Label>
                                            <Controller
                                                name="EntityTypes.0"
                                                control={control}
                                                render={({ field }) => (
                                                    <Select value={field.value || ''} onValueChange={(value) => {
                                                        field.onChange(value)
                                                        setValue('EntityTypes', [value])
                                                        // Reset template selection when entity type changes
                                                        setValue('RegularReminder.ReminderTemplateId', '')
                                                        // For internal users, set trigger type based on entity type
                                                        if (isInternalUsersCategory && value === NotificationEntityType.EXPENSE_CLAIM) {
                                                            setValue('RegularReminder.ReminderTrigger.TriggerType', ReminderTriggerPeriod.AfterSubmission)
                                                        }
                                                    }}>
                                                        <SelectTrigger>
                                                        <SelectValue />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                        <SelectItem value={NotificationEntityType.TIMESHEET}>{t("dashboard.createReminder.timesheets")}</SelectItem>
                                                        <SelectItem value={NotificationEntityType.EXPENSE_CLAIM}>{t("dashboard.createReminder.expenses")}</SelectItem>
                                                        <SelectItem value={NotificationEntityType.TIMESHEET_EXPENSE}>{t("dashboard.createReminder.timesheetsAndExpenses")}</SelectItem>
                                                        </SelectContent>
                                                    </Select>
                                                )}
                                            />
                                            </div>
                                        )}

                                        {/* Who - Different logic for workers vs clients vs internal */}
                                        {isWorkerCategory && (
                                            <div className="space-y-2">
                                            <Label htmlFor="add-reminder-who">{t("dashboard.createReminder.whoLabel")}</Label>
                                            <Controller 
                                                name="RegularReminder.RecipientGroupCodes.0"
                                                control={control}
                                                render={({ field }) => (
                                                    <Select value={field.value} onValueChange={(value) => {
                                                        field.onChange(value)
                                                        setValue('RegularReminder.RecipientGroupCodes', [value])
                                                    }}>
                                                        <SelectTrigger>
                                                            <SelectValue />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            <SelectItem value={ReminderRecipientGroupCodes.AllWorkers}>
                                                                <span>{t("dashboard.createReminder.allWorkers")}</span>
                                                            </SelectItem>
                                                            <SelectItem value={ReminderRecipientGroupCodes.OnlineTimesheetWorkers}>
                                                                <span>{t("dashboard.createReminder.workersOnlineTimesheets")}</span>
                                                            </SelectItem>
                                                            <SelectItem value={ReminderRecipientGroupCodes.OfflineTimesheetWorks}>
                                                                <span>{t("dashboard.createReminder.workersOfflineTimesheets")}</span>
                                                            </SelectItem>
                                                            <SelectItem value={ReminderRecipientGroupCodes.VmsTimesheetWorkers}>
                                                                <span>{t("dashboard.createReminder.workersVmsTimesheets")}</span>
                                                            </SelectItem>
                                                        </SelectContent>
                                                    </Select>
                                                )}
                                            />
                                            </div>
                                        )}

                                        {/* Internal Recipients - Checkboxes and additional email */}
                                        {isInternalUsersCategory && (
                                            <div className="space-y-3">
                                                <Label htmlFor="add-reminder-who">{t("dashboard.createReminder.whoLabel")}
                                                    <span className="text-sm text-red-500 ml-1">*</span>
                                                </Label>
                                                
                                                {/* Checkboxes for Internal recipients */}
                                                <div className="space-y-2">
                                                    {getInternalRecipientCheckboxOptions().map((option) => (
                                                        <div key={option.value} className="flex items-center space-x-2">
                                                            <Controller
                                                                name={`InternalRecipientCheckboxes.${option.value}` as any}
                                                                control={control}
                                                                render={({ field }) => (
                                                                    <Checkbox
                                                                        id={`internal-recipient-${option.value}`}
                                                                        checked={field.value || false}
                                                                        onCheckedChange={(checked) => field.onChange(checked)}
                                                                    />
                                                                )}
                                                            />
                                                            <Label 
                                                                htmlFor={`internal-recipient-${option.value}`} 
                                                                className="text-sm font-normal cursor-pointer"
                                                            >
                                                                {option.label}
                                                            </Label>
                                                        </div>
                                                    ))}
                                                </div>

                                                {/* Additional Email Input */}
                                                <div className="space-y-2">
                                                    <Controller
                                                        name="RegularReminder.AdditionalRecipientEmails"
                                                        control={control}
                                                        render={({ field }) => (
                                                            <EmailChipsInput
                                                                id="internal-additional-emails"
                                                                label={t("dashboard.headings.additionalEmail")}
                                                                placeholder={t("dashboard.headings.additionalEmailPlaceholder")}
                                                                value={field.value || []}
                                                                onChange={field.onChange}
                                                                maxEmails={5}
                                                            />
                                                        )}
                                                    />
                                                </div>
                                            </div>
                                        )}

                                        {/* When - Different behavior for workers vs clients */}
                                        <div className="space-y-3">
                                            <Label>{t("dashboard.createReminder.whenLabel")}</Label>
                                            
                                            {/* Internal Users - Multi-line format with different arrangement */}
                                            {isInternalUsersCategory ? (
                                                <>
                                                    <div className="flex items-center gap-2">
                                                        {selectedWhat === NotificationEntityType.EXPENSE_CLAIM ? (
                                                            // Show only text for Expense Claims
                                                            <span className="text-sm text-gray-700">
                                                                {t("dashboard.trigger.waitingForApproval")}
                                                            </span>
                                                        ) : (
                                                            // Show dropdown for other entity types
                                                            <Controller
                                                                name="RegularReminder.ReminderTrigger.TriggerType"
                                                                control={control}
                                                                render={({ field }) => (
                                                                    <Select value={field.value || ""} onValueChange={(value) => {
                                                                        field.onChange(value)
                                                                        // Reset template selection when trigger type changes for Internal users
                                                                        // because different trigger types have different available templates
                                                                        setValue('RegularReminder.ReminderTemplateId', '')
                                                                    }}>
                                                                        <SelectTrigger className="flex-1">
                                                                            <SelectValue />
                                                                        </SelectTrigger>
                                                                        <SelectContent>
                                                                            <SelectItem value={ReminderTriggerPeriod.AfterPeriodEnds}>
                                                                                {t("dashboard.trigger.waitingForSubmission")}
                                                                            </SelectItem>
                                                                            <SelectItem value={ReminderTriggerPeriod.AfterSubmission}>
                                                                                {t("dashboard.trigger.waitingForApproval")}
                                                                            </SelectItem>
                                                                        </SelectContent>
                                                                    </Select>
                                                                )}
                                                            />
                                                        )}
                                                        <span className="text-sm text-gray-700">for</span>
                                                        <span className="text-sm text-red-500">*</span>
                                                        <Input
                                                            type="number"
                                                            min="0"
                                                            {...register("RegularReminder.ReminderTrigger.NumberOfUnit", { valueAsNumber: true })}
                                                            className="min-w-11 max-w-14 no-spinners"
                                                        />
                                                        <span className="text-sm text-gray-700">business days,</span>
                                                    </div>

                                                    <div className="flex items-center gap-2">
                                                        <span className="text-sm text-gray-700">send at</span>
                                                        <Controller
                                                            name="RegularReminder.ReminderTrigger.Time"
                                                            control={control}
                                                            render={({ field }) => (
                                                                <Select value={field.value} onValueChange={field.onChange}>
                                                                    <SelectTrigger className="w-28">
                                                                        <SelectValue />
                                                                    </SelectTrigger>
                                                                    <SelectContent className="h-40 overflow-y-auto">
                                                                        {timeOptions.map((time) => (
                                                                            <SelectItem key={time.value} value={time.value}>
                                                                                {time.label}
                                                                            </SelectItem>
                                                                        ))}
                                                                    </SelectContent>
                                                                </Select>
                                                            )}
                                                        />
                                                        <Controller
                                                            name="RegularReminder.ReminderTrigger.TimeZone"
                                                            control={control}
                                                            render={({ field }) => (
                                                                <Select value={field.value} onValueChange={field.onChange}>
                                                                    <SelectTrigger className="flex-1">
                                                                        <SelectValue />
                                                                    </SelectTrigger>
                                                                    <SelectContent className="max-h-[300px] overflow-y-auto">
                                                                        {timezoneOptions.map((tz) => (
                                                                            <SelectItem key={tz.value} value={tz.value}>
                                                                                {tz.label} ({tz.offset})
                                                                            </SelectItem>
                                                                        ))}
                                                                    </SelectContent>
                                                                </Select>
                                                            )}
                                                        />
                                                    </div>
                                                </>
                                            ) : (
                                                /* Workers and Clients - Original layout */
                                                <>
                                                    <div className="flex items-center gap-2">
                                                        <Calendar className="w-4 h-4 text-gray-500" />
                                                        <span className="text-sm text-red-500">*</span>
                                                        <Input
                                                            type="number"
                                                            min="0"
                                                            {...register("RegularReminder.ReminderTrigger.NumberOfUnit", { valueAsNumber: true })}
                                                            className="min-w-11 max-w-14 no-spinners" 
                                                        />
                                                        {
                                                        isWorkerCategory? <span className="text-sm text-gray-700">{t("dashboard.createReminder.businessDays")} </span>
                                                            : <span className="text-sm text-gray-700">{t("dashboard.createReminder.businessDaysComma")} </span>
                                                        }
                                                        {isWorkerCategory ? (
                                                            <Controller
                                                                name="RegularReminder.ReminderTrigger.TriggerType"
                                                                control={control}
                                                                render={({ field }) => (
                                                                    <Select value={field.value || ""} onValueChange={field.onChange}>
                                                                    <SelectTrigger className="flex-1">
                                                                        <SelectValue />
                                                                    </SelectTrigger>
                                                                    <SelectContent>
                                                                        <SelectItem value={ReminderTriggerPeriod.BeforePeriodEnds}>
                                                                            {t("dashboard.createReminder.beforePeriodEnds")}
                                                                        </SelectItem>
                                                                        <SelectItem value={ReminderTriggerPeriod.AfterPeriodEnds}>
                                                                            {t("dashboard.createReminder.afterPeriodEnds")}
                                                                        </SelectItem>
                                                                    </SelectContent>
                                                                    </Select>
                                                                )}
                                                            />
                                                        ) : (
                                                            <span className="text-sm text-gray-700 -ml-1">{t("dashboard.createReminder.afterSubmission")}</span>
                                                        )}
                                                    </div>

                                                    <div className="flex items-center gap-2">
                                                        <Clock className="w-4 h-4 text-gray-500" />
                                                        <Controller
                                                            name="RegularReminder.ReminderTrigger.Time"
                                                            control={control}
                                                            render={({ field }) => (
                                                                <Select value={field.value} onValueChange={field.onChange}>
                                                                    <SelectTrigger className="w-32">
                                                                        <SelectValue />
                                                                    </SelectTrigger>
                                                                    <SelectContent className="h-40 overflow-y-auto">
                                                                        {timeOptions.map((time) => (
                                                                            <SelectItem key={time.value} value={time.value}>
                                                                                {time.label}
                                                                            </SelectItem>
                                                                        ))}
                                                                    </SelectContent>
                                                                </Select>
                                                            )}
                                                        />
                                                        <Controller
                                                            name="RegularReminder.ReminderTrigger.TimeZone"
                                                            control={control}
                                                            render={({ field }) => (
                                                                <Select value={field.value} onValueChange={field.onChange}>
                                                                <SelectTrigger className="flex-1">
                                                                    <SelectValue />
                                                                </SelectTrigger>
                                                                <SelectContent className="max-h-[300px] overflow-y-auto">
                                                                    {timezoneOptions.map((tz) => (
                                                                        <SelectItem key={tz.value} value={tz.value}>
                                                                        {tz.label} ({tz.offset})
                                                                        </SelectItem>
                                                                    ))}
                                                                </SelectContent>
                                                                </Select>
                                                            )}
                                                        />
                                                    </div>
                                                </>
                                            )}

                                            <div className="flex items-center gap-2">
                                                <ul className="list-disc list-inside">
                                                    <li className="text-xs text-gray-500">
                                                        {t("dashboard.alerts.reminderSentDaily1")}
                                                    </li>
                                                    <li className="text-xs text-gray-500 mt-1">
                                                        {
                                                            isWorkerCategory ? t("dashboard.alerts.reminderSentDailySubmitted") 
                                                            : isInternalUsersCategory ? t("dashboard.alerts.reminderSentDailySubmittedAndApproved")
                                                            : t("dashboard.alerts.reminderSentDailyApproved")
                                                        }
                                                    </li>
                                                </ul>
                                            </div>
                                        </div>

                                        {/* Template */}
                                        <div className="space-y-2">
                                        <Label htmlFor="add-reminder-template">{t("dashboard.createReminder.templateLabel")}</Label>
                                        <Controller
                                            name="RegularReminder.ReminderTemplateId"
                                            control={control}
                                            render={({ field }) => (
                                                <Select value={field.value || ""} onValueChange={field.onChange} disabled={templatesLoading}>
                                                    <SelectTrigger>
                                                    <SelectValue placeholder={
                                                        templatesLoading ? (
                                                            <span className="flex items-center gap-2">
                                                                <Loader2 className="w-4 h-4 animate-spin" />
                                                                {t("dashboard.createReminder.loadingTemplates")}
                                                            </span>
                                                        ) : (
                                                            <span>
                                                                {t("dashboard.createReminder.selectTemplateRequired")} <span className="text-red-500">*</span>
                                                            </span>
                                                        )
                                                    } />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                    {getAvailableTemplatesForEntityTypes(watch('EntityTypes'), ReminderType.REGULAR).map((template) => (
                                                        <SelectItem key={template.id ?? ''} value={template.id ?? ''}>
                                                            {`${template.Data?.TemplateName}`}
                                                        </SelectItem>
                                                    ))}
                                                    </SelectContent>
                                                </Select>
                                            )}
                                        />
                                        {/* No templates available */}
                                        {!templatesLoading && getAvailableTemplatesForEntityTypes(watch('EntityTypes'), ReminderType.REGULAR).length === 0 && (
                                            <p className="text-sm text-gray-500">
                                                {templates.length === 0 
                                                    ? t("dashboard.createReminder.noTemplatesConfigured")
                                                    : t("dashboard.createReminder.noTemplatesAvailable")
                                                }
                                                {templates.length > 0 && selectedRecipientType === RecipientType.WORKER && t("dashboard.createReminder.onlyTimesheetTemplatesWorkers")}
                                                {templates.length > 0 && selectedRecipientType === RecipientType.CLIENT && selectedWhat === NotificationEntityType.TIMESHEET && t("dashboard.createReminder.onlyTimesheetTemplatesClients")}
                                                {templates.length > 0 && selectedRecipientType === RecipientType.CLIENT && selectedWhat === NotificationEntityType.EXPENSE_CLAIM && t("dashboard.createReminder.onlyExpenseTemplatesClients")}
                                                {templates.length > 0 && selectedRecipientType === RecipientType.CLIENT && selectedWhat === NotificationEntityType.TIMESHEET_EXPENSE && t("dashboard.createReminder.onlyBothTemplatesClients")}
                                            </p>
                                        )}
                                        </div>
                                    </div>
                                </div>

                                {/* Right Side - Preview */}
                                <div className="w-4/6 bg-gray-50 overflow-y-auto flex flex-col">
                                    {/* Preview Content */}
                                    <div className="flex-1 flex flex-col">
                                        {/* Preview Header with Edit Button */}
                                        {(
                                            <div className="bg-white border-b px-6 py-4">
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center gap-4">
                                                        <span className="text-gray-600 font-medium">{t("dashboard.createReminder.preview")}</span>
                                                    </div>
                                                    <div className="flex items-center gap-4">
                                                        <div className="flex items-center gap-2">
                                                            <Switch checked={withSampleData} onCheckedChange={setWithSampleData} />
                                                            <span className="text-sm text-gray-600">{t("dashboard.createReminder.withSampleData")}</span>
                                                        </div>
                                                        <Button
                                                            type="button"
                                                            variant="outline"
                                                            size="sm"
                                                            className="text-blue-600 border-blue-200 hover:bg-blue-50 bg-transparent"
                                                            onClick={() => {
                                                                const templateId = selectedTemplateId
                                                                const template = templates.find((t) => t.id === templateId)
                                                                if (template) {
                                                                    onEditMessage(template, EditMessageContext.Create)
                                                                }
                                                            }}
                                                            disabled={!selectedTemplateId}
                                                        >
                                                            <span className="mr-1">✏️</span>
                                                            {t("dashboard.createReminder.edit")}
                                                        </Button>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {/* Preview Body */}
                                        <div className="flex-1 bg-gray-50 overflow-x-hidden">
                                            {templatesLoading ? (
                                                <div className="text-center py-8 text-gray-500">
                                                    <Loader2 className="w-12 h-12 mx-auto mb-4 animate-spin" />
                                                    <p>{t("dashboard.createReminder.loadingTemplates")}</p>
                                                </div>
                                            ) : (selectedTemplateId) ? (
                                                  (() => {
                                                      const baseTemplateStructure = baseTemplate ? getEnglishTemplate(baseTemplate)?.Body : ""
                                                      const templateId = selectedTemplateId
                                                      const template = templates.find(t => t.id === templateId)
                                                      
                                                      if (!template) return null
                                                      
                                                      const englishTemplate = getEnglishTemplate(template)
                                                      const rawSubject = englishTemplate?.Subject ?? ""
                                                      const rawContent = englishTemplate?.Body ?? ""
                                                      
                                                      // Process template content with meta tags
                                                      const { subject, content } = processTemplateContent(template, rawSubject, rawContent, withSampleData, language)
                                                      
                                                      // Chrome first, then content — the same order the processor sends in, so tenant-authored
                                                      // content mentioning the platform address previews exactly as it will be delivered.
                                                      const injectedContent = applySupportEmailPlaceholder(
                                                          applySupportEmail(baseTemplateStructure ?? '', feedbackEmailAddress)
                                                          .replace(BaseTemplateTags.content, content)
                                                          .replace(BaseTemplateTags.subject, subject), feedbackEmailAddress)

                                                      return <div dangerouslySetInnerHTML={{ __html: injectedContent }} />
                                                  })()
                                              ) : (
                                                <div className="text-center py-8 text-gray-500">
                                                    <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                                                    <p>{t("dashboard.createReminder.noTemplateSelected")}</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            {/* Footer Actions - Always visible */}
                            <div className="flex justify-end items-center px-6 py-4 border-t bg-gray-50 flex-shrink-0">
                                <div className="flex gap-3">
                                <Button type="button" variant="outline" onClick={() => setAddReminderOpen(false)}>
                                    {t("dashboard.createReminder.cancel")}
                                </Button>
                                <Button
                                    type="submit"
                                    disabled={isSubmitDisabled}
                                    className="bg-slate-800 hover:bg-slate-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                                    >
                                    {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : t("dashboard.createReminder.createReminder")}
                                </Button>
                                </div>
                            </div>
                        </div>
                    </form>
                </FormProvider>
            </DialogContent>
        </Dialog>
    )
}